// jersey-print.js
// Name & number print add-on. The print is its own (hidden) product, added to the
// cart next to the jersey. Both lines carry the same `_print_ref` property so the
// cart can keep them paired, and a summary is mirrored into the cart note so the
// ERP gets the name/number even if it ignores line item properties.
window.JerseyPrint = {
  REF_KEY: '_print_ref',
  ADDON_KEY: '_print_addon',
  NAME_KEY: 'Name',
  NUMBER_KEY: 'Number',
  NOTE_PREFIX: 'PRINT: ',

  refOf(item) {
    return (item.properties && item.properties[this.REF_KEY]) || null;
  },

  isAddon(item) {
    return Boolean(item.properties && item.properties[this.ADDON_KEY]);
  },

  buildItems({ variantId, printVariantId, quantity, name, number }) {
    // Deterministic, so adding the same jersey + print again merges both lines.
    const ref = `${variantId}:${name}:${number}`;
    const properties = {
      [this.NAME_KEY]: name,
      [this.NUMBER_KEY]: number,
      [this.REF_KEY]: ref,
    };
    return [
      { id: Number(variantId), quantity, properties },
      {
        id: Number(printVariantId),
        quantity,
        properties: { ...properties, [this.ADDON_KEY]: '1' },
      },
    ];
  },

  // Line quantities that put the cart back into a valid state: each print line
  // matches its jersey's quantity, and a jersey whose print is gone is removed.
  getRepairs(cart) {
    const groups = new Map();
    cart.items.forEach((item) => {
      const ref = this.refOf(item);
      if (!ref) return;
      if (!groups.has(ref)) groups.set(ref, { jerseys: [], addons: [] });
      groups.get(ref)[this.isAddon(item) ? 'addons' : 'jerseys'].push(item);
    });

    const updates = {};
    groups.forEach(({ jerseys, addons }) => {
      if (addons.length === 0) {
        jerseys.forEach((item) => (updates[item.key] = 0));
        return;
      }
      const jerseyQuantity = jerseys.reduce((sum, item) => sum + item.quantity, 0);
      addons.forEach((item, index) => {
        const quantity = index === 0 ? jerseyQuantity : 0;
        if (item.quantity !== quantity) updates[item.key] = quantity;
      });
    });
    return updates;
  },

  // Replaces the PRINT lines in the cart note, keeping anything else in it.
  buildNote(cart, updates = {}) {
    const printLines = cart.items
      .filter((item) => this.refOf(item) && !this.isAddon(item))
      .map((item) => ({
        item,
        quantity: item.key in updates ? updates[item.key] : item.quantity,
      }))
      .filter(({ quantity }) => quantity > 0)
      .map(({ item, quantity }) => {
        const title = [item.product_title, item.variant_title]
          .filter(Boolean)
          .join(' / ');
        return `${this.NOTE_PREFIX}${quantity} x ${title} / ${this.NAME_KEY}: ${item.properties[this.NAME_KEY]} / ${this.NUMBER_KEY}: ${item.properties[this.NUMBER_KEY]}`;
      });
    const otherLines = (cart.note || '')
      .split('\n')
      .filter((line) => !line.startsWith(this.NOTE_PREFIX));
    return [...otherLines, ...printLines].join('\n').trim();
  },

  // Applies repairs and refreshes the note in one request. Resolves to
  // { cart, repaired } (cart includes `sections` when requested), or null when
  // the cart was already in sync.
  async sync(cart, sections) {
    const updates = this.getRepairs(cart);
    const note = this.buildNote(cart, updates);
    const repaired = Object.keys(updates).length > 0;
    if (!repaired && note === (cart.note || '')) return null;

    // Quantities go as an array in cart line order.
    const body = { note };
    if (repaired) {
      body.updates = cart.items.map((item) =>
        item.key in updates ? updates[item.key] : item.quantity,
      );
    }
    if (sections) {
      body.sections = sections;
      body.sections_url = window.location.pathname;
    }
    const response = await fetch(`${routes.cart_update_url}`, {
      ...fetchConfig(),
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`Cart update failed (${response.status})`);
    return { cart: await response.json(), repaired };
  },

  async fetchCart() {
    const response = await fetch(`${routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });
    return response.json();
  },
};

// Catch carts that were changed outside the drawer (other tabs, checkout back button).
document.addEventListener('DOMContentLoaded', () => {
  if (!document.querySelector('[data-print-sync]')) return;
  JerseyPrint.fetchCart()
    .then((cart) => JerseyPrint.sync(cart))
    .then((result) => {
      if (result?.repaired) document.querySelector('cart-drawer-items')?.onCartUpdate();
    })
    .catch((e) => console.error(e));
});

if (!customElements.get('jersey-print')) {
  customElements.define(
    'jersey-print',
    class JerseyPrintPicker extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.maxNameLength = parseInt(this.dataset.nameMaxLength, 10) || 12;
        this.namePattern = new RegExp(`^[A-ZÆØÅÄÖÜÉ .'-]{1,${this.maxNameLength}}$`);
        this.fields = this.querySelector('[data-print-fields]');
        this.nameInput = this.querySelector('[data-print-name]');
        this.numberInput = this.querySelector('[data-print-number]');
        this.errorElement = this.querySelector('[data-print-error]');

        this.addEventListener('change', (event) => {
          if (event.target.matches('[data-print-toggle]')) this.onToggle({ fromUser: true });
        });
        this.nameInput.addEventListener('input', () =>
          this.sanitize(this.nameInput, (value) => this.cleanName(value)),
        );
        this.numberInput.addEventListener('input', () =>
          this.sanitize(this.numberInput, (value) => this.cleanNumber(value)),
        );
        [this.nameInput, this.numberInput].forEach((input) =>
          input.addEventListener('focus', () => this.showPreviewSlide()),
        );
        this.restoreFromUrl();
        // Also picks up values browsers restore on back navigation
        this.onToggle();
      }

      cleanName(value) {
        return value
          .toUpperCase()
          .replace(/[^A-ZÆØÅÄÖÜÉ .'-]/g, '')
          .slice(0, this.maxNameLength);
      }

      cleanNumber(value) {
        return value.replace(/\D/g, '').slice(0, 2);
      }

      // The print is mirrored to ?print_name=&print_number= so a refresh or shared link restores it.
      restoreFromUrl() {
        const params = new URLSearchParams(window.location.search);
        if (!params.has('print_name') && !params.has('print_number')) return;

        this.nameInput.value = this.cleanName(params.get('print_name') || '');
        this.numberInput.value = this.cleanNumber(params.get('print_number') || '');
        const customToggle = this.querySelector('[data-print-toggle][value="custom"]');
        if (customToggle) customToggle.checked = true;
      }

      writeUrl() {
        const url = new URL(window.location.href);
        if (this.enabled) {
          url.searchParams.set('print_name', this.nameInput.value);
          url.searchParams.set('print_number', this.numberInput.value);
        } else {
          url.searchParams.delete('print_name');
          url.searchParams.delete('print_number');
        }
        window.history.replaceState(window.history.state, '', url);
      }

      get enabled() {
        return this.querySelector('[data-print-toggle]:checked')?.value === 'custom';
      }

      onToggle({ fromUser = false } = {}) {
        this.fields.hidden = !this.enabled;
        this.showError(false);
        this.updatePreview();
        if (!fromUser) return;
        this.writeUrl();
        if (this.enabled) this.showPreviewSlide();
      }

      sanitize(input, clean) {
        const value = clean(input.value);
        if (value !== input.value) input.value = value;
        this.showError(false);
        this.updatePreview();
        this.writeUrl();
      }

      // The overlay lives in the product media gallery (product-media-gallery-new.liquid).
      // The loop carousel clones slides, so every copy is updated.
      updatePreview() {
        const name = this.enabled ? this.nameInput.value : '';
        const number = this.enabled ? this.numberInput.value : '';
        document
          .querySelectorAll('[data-print-preview-name]')
          .forEach((element) => (element.textContent = name));
        document
          .querySelectorAll('[data-print-preview-number]')
          .forEach((element) => (element.textContent = number));
      }

      showPreviewSlide() {
        const index = parseInt(this.dataset.previewSlide, 10);
        if (Number.isNaN(index)) return;
        document.querySelector('#image-carousel')?.splide?.go(index);
      }

      // Returns { name, number }, or null (showing an error) when the fields are invalid.
      getSelection() {
        const name = this.nameInput.value.trim();
        const number = this.numberInput.value.trim();
        const nameValid = this.namePattern.test(name);
        const numberValid = /^\d{1,2}$/.test(number);
        if (nameValid && numberValid) return { name, number };

        this.showError(true);
        (nameValid ? this.numberInput : this.nameInput).focus();
        return null;
      }

      showError(visible) {
        this.errorElement.hidden = !visible;
      }
    },
  );
}
